import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";

import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable, type DataTableFilteredRequest } from "../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../state/store";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useOptions } from "../../components/Hooks/useOptions";
import { useOnlineStatus } from "../../components/Hooks/useOnlineStatus";
import { sumCachedLoads } from "../../offline/patchCachedDiggingSummary";
import { DeleteButton } from "../../components/Buttons/DeleteButton";
import { InsertGeoCrushingRow } from "./InsertGeologyCrushingRow";
import { GeoCrushingLoaderDTO } from "../../DTOs/Geology/GeoCrushingLoaderDTO";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { TripleShift } from "../../types/Shift";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import LoaderCell from "./TableCellsComponents/LoaderCell";
import MovementToCell from "./TableCellsComponents/MovementToCell";
import MovementFromCell from "./TableCellsComponents/MovementFromCell";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { GetGeoRehandlesPagedResultDTO } from "../../DTOs/Geology/GetGeoRehandlesPagedResultDTO";
import GeologyCrushingTableFooter from "./GeologyCrushingTableFooter";
import GeologyCrushingTableNoResultsContent from "./GeologyCrushingTableNoResultsContent";
import { TablePage } from "../../components/Layout/TablePage";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { geoRehandleMutations } from "../../offline/geoRehandleMutations";

type GeologyCrushingTableMetaData = {
  totalLoads: number
  totalTonnes: number
}

const GeologyCrushing: React.FC = () => {
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const canToggleLock = useSelector((s: RootState) => s.shift.canToggleLock);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();
  const queryClient = useQueryClient();
  const { selectedDate, selectedShift } = useShiftTimes();
  const isOnline = useOnlineStatus();
  const [localSelectedShift, _setLocalSelectedShift] = useState<TripleShift>(selectedShift)
  const [insertRowState, setInsertRowState] = useState<GeoRehandleDTO>(new GeoRehandleDTO({
    geoRehandleId: 0,
    operationId: 0,
    bucketTypeId: 0,
    movementFromId: 0,
    movementToId: 0
  }));

  const setLocalSelectedShift = (newShift: TripleShift) => {
    _setLocalSelectedShift(newShift);

    if (newShift === "DAY" || newShift === "NIGHT") {
      dispatch(setSelectedShift(newShift));
    }
  };

  const {
    options: crushingMovementFromLocationOptions,
    isLoading: isCrushingMovementFromLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['geology', 'crushingFromLocations', selectedDate.toISODate()],
    () => geologyAjax.getCrushingMovementFromLocations(selectedDate),
    x => x,
  );

  const {
    options: crushingMovementToLocationOptions,
    isLoading: isCrushingMovementToLocationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['geology', 'crushingToLocations', selectedDate.toISODate()],
    () => geologyAjax.getCrushingMovementToLocations(selectedDate),
    x => x,
  );

  const {
    options: loaderOptions,
    isLoading: isLoaderOptionsLoading
  } = useOptions<GeoCrushingLoaderDTO, GeoCrushingLoaderDTO>(
    ['geology', 'crushingLoaders', selectedDate.toISODate(), localSelectedShift],
    () => geologyAjax.getCrushingLoaders(selectedDate, localSelectedShift),
    x => x,
  );

  const handleUpdateRow = useCallback(async (row: GeoRehandleDTO) => {
    let isValid = true;

    if (!row.bucketTypeId) {
      isValid = false;
      enqueueSnackbar('Please select a Loader', { variant: 'error' });
    }
    if (!row.operationId) {
      isValid = false;
      enqueueSnackbar('Please select a Loader', { variant: 'error' });
    }
    if (!row.movementFromId) {
      isValid = false;
      enqueueSnackbar('Please select a location to move from', { variant: 'error' });
    }
    if (!row.loads) {
      isValid = false;
      enqueueSnackbar('Please enter the number of loads to move', { variant: 'error' });
    }
    if (!row.movementToId) {
      isValid = false;
      enqueueSnackbar('Please select a Crushing site to move to', { variant: 'error' });
    }
    if (row.movementFromId === row.movementToId) {
      isValid = false;
      enqueueSnackbar('Please select a different location to move to', { variant: 'error' });
    }

    if (!isValid || row.geoRehandleId === 0) return;

    const response = await geoRehandleMutations.updateGeoRehandle(row)

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      // Note: a server rejection (e.g. a concurrency conflict) leaves the displayed value stale;
      // refetch so the user sees the current server values rather than their lost edit.
      invalidate();
      return false;
    }

    enqueueSnackbar('Crushing Entry updated successfully', { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    updateRowSource((r) => r.geoRehandleId === rehandle!.geoRehandleId, rehandle!, { totalLoads, totalTonnes });

    return true;
  }, [enqueueSnackbar]);

  const handleAddRow = useCallback(async () => {
    let isValid = true;

    if (!insertRowState.operationId) {
      enqueueSnackbar('Please select a Loader', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.bucketTypeId) {
      enqueueSnackbar('Please select a Loader', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.movementFromId) {
      enqueueSnackbar('Please select a location to move from', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.loads) {
      enqueueSnackbar('Please enter the number of loads to move', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!insertRowState.movementToId) {
      enqueueSnackbar('Please select a Crushing site to move to', { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }

    if (!isValid) return false;

    const response = await geoRehandleMutations.addGeoRehandle(insertRowState, selectedDate, localSelectedShift);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 * (index + 1) });
      });
      return false;
    }

    enqueueSnackbar('Crushing Entry added successfully', { variant: 'success' });

    const { rehandle, totalLoads, totalTonnes } = response.data!;
    appendRowSource(rehandle!, { totalLoads, totalTonnes });
    setInsertRowState(new GeoRehandleDTO({
      geoRehandleId: 0,
      operationId: 0,
      bucketTypeId: 0,
      movementFromId: 0,
      movementToId: 0
    }));

    return true;
  }, [insertRowState, localSelectedShift, selectedDate, enqueueSnackbar]);

  const deleteRow = useCallback(async (rowId: number) => {
    const response = await geoRehandleMutations.deleteGeoRehandle(rowId);

    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    const { totalLoads, totalTonnes } = response.data!;
    removeRowSource((r) => r.geoRehandleId === rowId, { totalLoads, totalTonnes });
    enqueueSnackbar('Crushing entry deleted successfully', { variant: 'success', autoHideDuration: 3000 })

    return true;
  }, [enqueueSnackbar]);

  const getRows = useCallback((req: DataTableFilteredRequest) => geologyAjax.getGeoCrushingEntries(selectedDate, localSelectedShift, req), [selectedDate, localSelectedShift]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        {
          name: "Loader",
          editable: false,
          type: "custom",
          value: entry.bucketTypeId,
          tailwindClasses: "basis-[20%] py-1 pl-1",
          render: () => (
            <LoaderCell
              entry={entry}
              loaderOptions={loaderOptions}
              isLoaderOptionsLoading={isLoaderOptionsLoading}
              handleUpdateRow={handleUpdateRow}
              isShiftLocked={isShiftLocked}
            />
          )
        },
        {
          name: "Movement From",
          editable: false,
          type: "custom",
          value: entry.movementFromId,
          tailwindClasses: "basis-[30%]",
          render:
            () => (
              <MovementFromCell
                entry={entry}
                crushingMovementFromLocationOptions={crushingMovementFromLocationOptions}
                isCrushingMovementFromLocationOptionsLoading={isCrushingMovementFromLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isShiftLocked={isShiftLocked}
              />
            )
        },
        {
          name: "Loads",
          editable: true,
          type: "integer",
          value: entry.loads,
          tailwindClasses: "basis-[10%] py-1",
          disabled: isShiftLocked,
          onChangeDebounced: async (_, newValue) => await handleUpdateRow({ ...entry, loads: newValue }),
          onChangeDebouncedMs: 1200,
        },
        {
          name: "Movement To",
          editable: false,
          type: "custom",
          value: entry.movementToId,
          tailwindClasses: "basis-[30%]",
          render:
            () => (
              <MovementToCell
                entry={entry}
                crushingMovementToLocationOptions={crushingMovementToLocationOptions}
                isCrushingMovementToLocationOptionsLoading={isCrushingMovementToLocationOptionsLoading}
                handleUpdateRow={handleUpdateRow}
                isShiftLocked={isShiftLocked}
              />
            )
        },
        {
          name: 'Actions',
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'basis-[10%] pr-1',
          render: (_, tableRow) =>
            <div className="flex justify-center flex-wrap gap-1">
              <DeleteButton
                disabled={isShiftLocked}
                handleDelete={() => deleteRow(tableRow.id)}
              />
            </div>
        }
      ]
    }
  }, [
    loaderOptions,
    isLoaderOptionsLoading,
    handleUpdateRow,
    isShiftLocked,
    crushingMovementFromLocationOptions,
    isCrushingMovementFromLocationOptionsLoading,
    crushingMovementToLocationOptions,
    isCrushingMovementToLocationOptionsLoading,
    deleteRow
  ]);

  const selectGeologyCrushingTableMetaData = useCallback((response: GetGeoRehandlesPagedResultDTO) => ({
    totalTonnes: response.totalTonnes,
    totalLoads: response.totalLoads
  }), []);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    sortField,
    sortDirection,
    setPage,
    appendRowSource,
    removeRowSource,
    updateRowSource,
    invalidate,
    tableMetaData
  } = useDataTable<GeoRehandleDTO, number, GeologyCrushingTableMetaData, GetGeoRehandlesPagedResultDTO>({
    queryKey: ['geology', 'crushing', selectedDate.toISODate(), localSelectedShift],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
    selectMeta: selectGeologyCrushingTableMetaData
  });

  useEffect(() => {
    const newView = "GeologyCrushing";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const handleGeoMovementsChanged = useCallback(() => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: ['geology', 'crushingFromLocations'] });
    queryClient.invalidateQueries({ queryKey: ['geology', 'crushingToLocations'] });
  }, [invalidate, queryClient]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleGeoMovementsChanged);
    return () => {
      connection.off("DataUpdatedAsync", handleGeoMovementsChanged);
    };
  }, [connection, handleGeoMovementsChanged]);

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => dispatch(setSelectedDate(date.toISODate()!))}
        />
        <ShiftSelectCrumb
          showAfternoonShift
          selectedShift={localSelectedShift}
          setSelectedShift={setLocalSelectedShift}
        />
        <ButtonCrumb buttonText={"Crushing"} />
      </Breadcrumbs>
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        error={error}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalPages={totalPages}
        totalCount={totalCount}
        setPage={setPage}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1080}
        fillHeight
        autoPageSize
        onAutoPageSize={onAutoPageSize}
        estimatedRowHeightPx={60}
        noResultsContent={<GeologyCrushingTableNoResultsContent
          isShiftLocked={isShiftLocked}
          canToggleLock={canToggleLock}
        />}
        tableFooterContent={<GeologyCrushingTableFooter
          totalLoads={isOnline
            ? tableMetaData?.totalLoads
            : sumCachedLoads(['dataTable', 'geology', 'crushing', selectedDate.toISODate(), localSelectedShift, 'all', false, null])}
          totalTonnes={tableMetaData?.totalTonnes}
          isLoading={isLoading}
        />}
        insertRowRenderer={() => <InsertGeoCrushingRow
          state={insertRowState}
          setState={setInsertRowState}
          loaderOptions={loaderOptions}
          isLoaderOptionsLoading={isLoaderOptionsLoading}
          crushingMovementFromLocationOptions={crushingMovementFromLocationOptions}
          isCrushingMovementFromLocationOptionsLoading={isCrushingMovementFromLocationOptionsLoading}
          crushingMovementToLocationOptions={crushingMovementToLocationOptions}
          isCrushingMovementToLocationOptionsLoading={isCrushingMovementToLocationOptionsLoading}
          onAdd={handleAddRow}
          isShiftLocked={isShiftLocked}
        />}
        overrideHeaderCells={[
          { name: "Loader", tailwindClasses: "basis-[20%] py-1 pl-1" },
          { name: "Movement From", tailwindClasses: "basis-[30%]" },
          { name: "Loads", tailwindClasses: "basis-[10%] py-1" },
          { name: "Movement To", tailwindClasses: "basis-[30%]" },
          { name: "Actions", tailwindClasses: "basis-[10%] pr-1" }
        ]}
      />
    </TablePage>
  )
}

export default GeologyCrushing;